// Jedno miejsce na polski zapis liczb, kwot i dat w calej aplikacji.
//
// Kwoty grupujemy zawsze (tez 4-cyfrowe: „4 860 zł”), bo w kolumnach i kafelkach
// liczby musza sie latwo porownywac. Intl dla pl-PL nie grupuje liczb < 10 000,
// dlatego grupowanie jest zrobione recznie.

const NBSP = ' ';

export const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const n = Number(value.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }
  if (value && typeof value === 'object' && 'toString' in value) {
    const n = Number(String(value));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const groupDigits = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

/** 41250.5 → „41 250,5”; decimals ustala stala liczbe miejsc po przecinku. */
export const formatNumber = (value: unknown, decimals?: number): string => {
  const n = toNumber(value);
  const places = decimals ?? (Number.isInteger(n) ? 0 : Math.min(2, (String(n).split('.')[1] || '').length));
  const [int, frac] = Math.abs(n).toFixed(places).split('.');
  const sign = n < 0 && Number(Math.abs(n).toFixed(places)) !== 0 ? '-' : '';
  return `${sign}${groupDigits(int)}${frac ? `,${frac}` : ''}`;
};

/**
 * Kwota w zlotych.
 * - domyslnie bez groszy, gdy kwota jest calkowita („200 zł”), inaczej z groszami („199,90 zł”);
 * - `decimals: 2` wymusza grosze (paragony, kasa), `decimals: 0` zaokrugla.
 */
export const formatMoney = (value: unknown, options: { decimals?: 0 | 2 } = {}): string => {
  const n = toNumber(value);
  const decimals = options.decimals ?? (Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2);
  return `${formatNumber(n, decimals)}${NBSP}zł`;
};

export const formatPercent = (value: unknown, decimals = 0): string => `${formatNumber(value, decimals)}%`;

const asDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('pl-PL', options);
const fLong = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fDayMonth = fmt({ day: 'numeric', month: 'long' });
const fDayMonthShort = fmt({ day: 'numeric', month: 'short' });
const fDate = fmt({ day: '2-digit', month: '2-digit', year: 'numeric' });
const fTime = fmt({ hour: '2-digit', minute: '2-digit' });
const fMonthYear = fmt({ month: 'long', year: 'numeric' });
const fWeekdayDay = fmt({ weekday: 'long', day: 'numeric', month: 'long' });

/** „sobota, 12 września 2026” */
export const formatDateLong = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fLong.format(d) : '—';
};
/** „sobota, 12 września” */
export const formatWeekdayDay = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fWeekdayDay.format(d) : '—';
};
/** „12 września” */
export const formatDayMonth = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fDayMonth.format(d) : '—';
};
/** „12 wrz” */
export const formatDayMonthShort = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fDayMonthShort.format(d) : '—';
};
/** „12.09.2026” */
export const formatDate = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fDate.format(d) : '—';
};
/** „08:52” */
export const formatTime = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fTime.format(d) : '—';
};
/** „12.09.2026, 08:52” */
export const formatDateTime = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? `${fDate.format(d)}, ${fTime.format(d)}` : '—';
};
/** „wrzesień 2026” */
export const formatMonthYear = (value: Date | string | number | null | undefined): string => {
  const d = asDate(value);
  return d ? fMonthYear.format(d) : '—';
};

/** 90 → „1 h 30 min”, 45 → „45 min” */
export const formatDuration = (minutes: unknown): string => {
  const total = Math.max(0, Math.round(toNumber(minutes)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}${NBSP}h ${m}${NBSP}min`;
  if (h) return `${h}${NBSP}h`;
  return `${m}${NBSP}min`;
};

/** Polska forma liczebnika: plural(5, 'wizyta', 'wizyty', 'wizyt') → „wizyt” */
export const plural = (n: number, one: string, few: string, many: string): string => {
  const abs = Math.abs(n);
  if (abs === 1) return one;
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

/** „5 wizyt” */
export const countLabel = (n: number, one: string, few: string, many: string): string => `${formatNumber(n)}${NBSP}${plural(n, one, few, many)}`;

/** Inicjaly z imienia i nazwiska albo e-maila. */
export const initials = (first?: string | null, last?: string | null, fallback?: string | null): string => {
  const a = (first || '').trim();
  const b = (last || '').trim();
  if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || '?';
  const f = (fallback || '').trim();
  return f ? f.charAt(0).toUpperCase() : '?';
};
